import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { first, tap, filter, map, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, Subscription, interval } from 'rxjs';
import { AuthService } from './auth.service';
import * as cuid from 'cuid';
import { WebSocketTicker, WebSocketStatus, WebSocketSubscriptionOptions, WebSocketEventType, WebSocketTrade, WebSocketOrder, WebSocketBalanceChange, WebSocketOrderBook } from '../models/wss';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  CONNECTION_CHECK_TIME = 15 * 1000;
  WEB_SOCKET_URL = environment.api.websocket;

  ws: WebSocketSubject<any>;
  status = new BehaviorSubject<WebSocketStatus>(WebSocketStatus.Idle);

  reconnection: Subject<void>;
  timer: Subscription;

  constructor(private auth: AuthService) {
  }

  buildWebSocket(requiesAuth = false) {
    const token = this.auth.getToken();

    if (this.ws) {
      this.ws.complete();
    }

    let url = this.WEB_SOCKET_URL;

    if (requiesAuth && token) {
      url += `?access_token=${token.accessToken}&session_id=${token.sessionId}`;
    }

    this.status.next(WebSocketStatus.Connecting);
    this.ws = webSocket({url});

    console.log('WSS:', 'Connected');
  }

  setStatus(status: WebSocketStatus) {
    this.status.next(status);
  }

  get statusCahnges(): Observable<WebSocketStatus> {
    return this.status.asObservable();
  }

  get connectionChanges() {
    if (!this.ws || this.ws.hasError || this.ws.isStopped) {
      throw Error('Please connect the WebSocket first');
    }

    return this.reconnection.asObservable();
  }

  connect(requiresAuth = false, keepAlive = true) {
    console.log('WSS:', 'Connecting');
    if (!this.ws || this.ws.hasError || this.ws.isStopped) {
      this.buildWebSocket(requiresAuth);
      this.setStatus(WebSocketStatus.Connecting);

      if (keepAlive) {
        this.reconnection = new Subject();
        // this.reconnection.next();
        this.keepConnection();
      }
    }
  }

  disconnect() {
    console.log('WSS:', 'Disconnecting');
    this.status.next(WebSocketStatus.Closed);

    if (this.timer) {
      this.timer.unsubscribe();
      this.timer = null;
    }

    if (this.reconnection) {
      this.reconnection.complete();
      this.reconnection = null;
    }

    if (this.ws) {
      this.ws.complete();
      this.ws = null;
    }

    this.setStatus(WebSocketStatus.Idle);
  }

  keepConnection(requiresAuth = false) {
    if (this.timer) {
      this.timer.unsubscribe();
    }

    let beat = false;
    let channelId: number;

    const hb = this.subscribe('heart-beat', null, {sendWholeResponse: true, filterOutConfirmationMessage: false}).subscribe(res => {
      console.log('Heart Beat', res.channelId);
      channelId = res.channelId;
      beat = true;
      this.status.next(WebSocketStatus.Connected);
    });

    this.timer = interval(this.CONNECTION_CHECK_TIME).subscribe({
      next: () => {
        console.log('Timer');
        if (beat) {
          beat = false;
        } else {
          console.log('Reset');
          this.status.next(WebSocketStatus.Closed);
          this.auth.refreshToken().subscribe(response => {
            if (this.ws) {
              this.ws.complete();
              this.ws = null;
            }

            this.buildWebSocket(true);
            this.setStatus(WebSocketStatus.Connecting);
            this.reconnection.next();
            this.keepConnection(requiresAuth);
          });
        }
      },
      error: () => {},
      complete: () => hb.unsubscribe()
    });
  }

  subscribe(channel: string, data: {[key: string]: any} = null, options: WebSocketSubscriptionOptions = null): Observable<any> {
    if (!this.ws || this.ws.hasError || this.ws.isStopped) {
      throw Error('Please connect the WebSocket first');
    }

    let channelId: number;
    options = options ? options : {};
    const event = WebSocketEventType.Subscribe;
    const uuid = options.uuid ? options.uuid : cuid();
    const message = {channel, event, uuid, ...data};

    return this.ws.multiplex(
      () => message,
      () => {
        if (channelId) {
          return {channel, channelId, event: `un${event}`};
        }
      },
      (res) => (res.event === event && res.uuid === uuid) || (!res.event && res.channelId === channelId)
    ).pipe(
      tap({
        next: response => {
          // console.log('WSS Next', response);
          channelId = response.channelId;
        },
        error: (error) => {
          console.log('WSS Error', error);
          return error;
        },
        complete: () => {
          // console.log('WSS Complete', channel, data, uuid, channelId);
        }
      }),
      filter(response => response.channelId === channelId && (!options.filterOutConfirmationMessage || response.data)),
      // debounceTime(300),
      map(response => options.sendWholeResponse ? response : response.data)
    );
  }

  publish(channel: string, channelId: number, data = {}, uuid = null, sendWholeResponse = false) {
    if (!this.ws || this.ws.hasError || this.ws.isStopped) {
      throw Error('Please connect the WebSocket first');
    }

    uuid = uuid ? uuid : cuid();
    const subscription = this.ws.pipe(
      filter(res => res.event === WebSocketEventType.Publish && res.channelId === channelId),
      map(res => sendWholeResponse ? res : res.data),
      first()
    );
    this.ws.next({channel, channelId, uuid, event: WebSocketEventType.Publish, data});
    return subscription;
  }

  ticker(pair: string): Observable<WebSocketTicker> {
    return this.subscribe('ticker', {pair});
  }

  trades(pair: string): Observable<WebSocketTrade[] | WebSocketTrade> {
    return this.subscribe('trades', {pair});
  }

  orderBook(pair: string, precision: number): Observable<WebSocketOrderBook> {
    return this.subscribe('orderbook', {pair});
  }

  orderHistory(precision: number): Observable<WebSocketOrder[]> {
    return this.subscribe('user-orders', {precision});
  }

  balances(): Observable<WebSocketBalanceChange> {
    return this.subscribe('user-balances');
  }

  // tslint:disable-next-line: max-line-length
  priceHistorySubscription(pair: string, resolution: number,  options: WebSocketSubscriptionOptions = null): Observable<any> {
    return this.subscribe('price-history', {pair, resolution}, options);
  }

  priceHistoryPublish(channelId: number, from: number = null, to: number = null): Observable<any> {
    return this.publish('price-history', channelId, {
      event: 'getHistoryData',
      from: from,
      to: to
    });
  }

  priceHistory(pair: string, resolution: number, from: number = null, to: number = null,  uuid = null): Observable<any> {
    return this.priceHistorySubscription(pair, resolution, {uuid, sendWholeResponse: true, filterOutConfirmationMessage: false}).pipe(
      first(),
      switchMap(res => {
        return this.priceHistoryPublish(res.channelId, from, to);
      }),
    );
  }

  realTimeCandles(pair: string, interval: number,  options: WebSocketSubscriptionOptions = null): Observable<any> {
    return this.subscribe('candles', {pair, interval}, options);
  }
}
